import type { JSX } from 'react';
import { NavLink } from 'react-router-dom';
import { Chip } from '@heroui/react';

import type { RefArchManifestEntry } from '~/types';

import { formatRefArchTitle } from '../utils';

interface RefArchCardProps {
  refArch: RefArchManifestEntry;
}

const ACCENT_COLORS: Record<string, string> = {
  'api-service': 'border-l-blue-500',
  'web-application': 'border-l-emerald-500',
  'ai-agent-system': 'border-l-purple-500',
  'event-driven': 'border-l-amber-500',
  serverless: 'border-l-cyan-500',
  infrastructure: 'border-l-slate-500',
  'data-platform': 'border-l-orange-500',
  'workflow-automation': 'border-l-rose-500',
  'desktop-application': 'border-l-indigo-500',
  'mobile-application': 'border-l-pink-500',
  monorepo: 'border-l-teal-500',
  'cli-tool': 'border-l-lime-500',
};

const MAX_QUALITY_ATTRIBUTES = 3;

export function RefArchCard({ refArch }: RefArchCardProps): JSX.Element {
  const accent = ACCENT_COLORS[refArch.architecture_type] ?? 'border-l-default';
  const topAttributes = Object.entries(refArch.quality_profile)
    .filter(([, level]) => level === 'critical' || level === 'high')
    .slice(0, MAX_QUALITY_ATTRIBUTES);

  return (
    <NavLink
      to={`/patterns/architecture/${refArch.ref_arch_id}`}
      className={`block p-5 rounded-lg border border-default border-l-4 ${accent} bg-surface hover:bg-surface-elevated transition-colors`}
    >
      <h3 className="text-base font-semibold text-foreground mb-1">
        {formatRefArchTitle(refArch.variant)}
      </h3>

      <p className="text-xs text-muted line-clamp-2 mb-3">{refArch.description}</p>

      <div className="flex items-center gap-2 flex-wrap">
        <Chip size="sm" variant="bordered">
          {refArch.pattern_ids.length} patterns
        </Chip>
        {topAttributes.map(([attr, level]) => (
          <Chip
            key={attr}
            size="sm"
            variant="flat"
            color={level === 'critical' ? 'danger' : 'success'}
          >
            {attr}
          </Chip>
        ))}
      </div>
    </NavLink>
  );
}
